import { Button, Classes } from "@blueprintjs/core";
import { useNavigate } from "react-router";

import { useWorkspaceSlug } from "@/components/helpers/custom-hooks/use-workspace-slug";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { buildCreateScenarioUrl } from "@/components/pages_logged_in/roleplays/utils";
import Filter from "@/components/pages_logged_in/workspace/pieces/Filter";
import FilterButton from "@/components/pages_logged_in/workspace/pieces/FilterButton";
import SearchBox from "@/components/pages_logged_in/workspace-admin/SearchBox";
import SettingSvg from "@/components/pieces/icons/svg/setting";
import { QueryScenariosStatus, VisibilityScope } from "@/graphql";

export const ScopeFilterOptions = [
  {
    id: -1,
    value: null,
    text: "All",
  },
  {
    id: VisibilityScope.User,
    value: VisibilityScope.User,
    text: "Personal",
  },
  {
    id: VisibilityScope.Workspace,
    value: VisibilityScope.Workspace,
    text: "Workspace",
  },
  {
    id: VisibilityScope.Global,
    value: VisibilityScope.Global,
    text: "Global",
  },
];

const StatusFilterOptions = [
  {
    id: 0,
    text: "All",
    value: QueryScenariosStatus.All,
  },
  {
    id: 1,
    text: "Incomplete",
    value: QueryScenariosStatus.Incomplete,
  },
  {
    id: 2,
    text: "Complete",
    value: QueryScenariosStatus.Complete,
  },
];

export function ScenariosListHeader({
  title,
  loading = false,
  statusFilter = null,
  onChangeStatusFilter = null,
  scopeFilter = null,
  onChangeScopeFilter = null,
  onSearch = null,
  allowAdd = false,
}) {
  const workspaceSlug = useWorkspaceSlug();
  const navigate = useNavigate();
  if (loading) {
    return (
      <Flex fontWeight={500} mb={24} justifyContent="space-between">
        <Box height={24} width={200} className={Classes.SKELETON} />
        <Box height={24} width={200} className={Classes.SKELETON} />
      </Flex>
    );
  }

  const onChangeStatus = (id) => {
    const option = StatusFilterOptions.find((x) => x.id === id);
    if (onChangeStatusFilter) {
      onChangeStatusFilter(option?.value);
    }
  };

  const isSelectStatusFn = (id) => {
    const option = StatusFilterOptions.find((x) => x.id === id);
    return option.value === statusFilter;
  };

  const currentStatus = StatusFilterOptions.find(
    (x) => x.value === statusFilter,
  );

  const borderStyle = {
    border: "0.5px solid #E2E2E2",
    marginRight: 10,
    padding: "10px 24px",
    minWidth: 120,
    width: "100%",
  };

  const availableScopeFiltersOptions = workspaceSlug
    ? ScopeFilterOptions
    : ScopeFilterOptions.filter(
        (scope) => scope.id !== VisibilityScope.Workspace,
      );

  const currentScope = availableScopeFiltersOptions.find(
    (x) => x.id === scopeFilter,
  );

  const isSelectScopeFn = (id) => {
    return id === scopeFilter;
  };

  const onChangeScope = (id) => {
    const option = availableScopeFiltersOptions.find((x) => x.id === id);
    if (onChangeScopeFilter) {
      onChangeScopeFilter(option?.id);
    }
  };

  return (
    <>
      <Media greaterThanOrEqual="lg">
        <Flex
          fontWeight={500}
          justifyContent="space-between"
          mt={4}
          mb={3}
          width={title ? "100%" : "auto"}
          alignItems="center"
        >
          {title && (
            <Box fontSize={24} fontWeight={600}>
              {title}
            </Box>
          )}
          <Flex
            justifyContent="space-between"
            className={`${title ? "initial" : "w-full"}`}
          >
            <Flex>
              {statusFilter && (
                <Filter
                  menuPosition="bottom-right"
                  fullHeight={true}
                  button={
                    <FilterButton
                      style={borderStyle}
                      rightIcon={<SettingSvg />}
                      text={currentStatus?.text}
                    />
                  }
                  items={StatusFilterOptions}
                  isSelectedFn={isSelectStatusFn}
                  multiple={false}
                  onSelect={onChangeStatus}
                  viewMode="radio"
                />
              )}
              {scopeFilter && (
                <Filter
                  menuPosition="bottom-right"
                  fullHeight={true}
                  button={
                    <FilterButton
                      style={borderStyle}
                      rightIcon={<SettingSvg />}
                      text={currentScope?.text}
                    />
                  }
                  items={availableScopeFiltersOptions}
                  isSelectedFn={isSelectScopeFn}
                  multiple={false}
                  onSelect={onChangeScope}
                  viewMode="radio"
                />
              )}
              {onSearch && (
                <Box ml={1}>
                  <SearchBox
                    style={{
                      borderRadius: 6,
                      border: "0.5px solid #E2E2E2",
                      overflow: "hidden",
                      padding: "22px 0px 22px 36px",
                      fontSize: 16,
                      width: 220,
                    }}
                    className="icon-center"
                    disabled={loading}
                    refine={onSearch}
                    throttledDuration={400}
                    searchPlaceholder="Search scenarios"
                  />
                </Box>
              )}
            </Flex>
            {allowAdd && (
              <Button
                intent="primary"
                icon="plus"
                style={{
                  fontSize: 15,
                  marginLeft: 10,
                  minWidth: "fit-content",
                }}
                onClick={() => {
                  navigate(buildCreateScenarioUrl());
                }}
              >
                Create Scenario
              </Button>
            )}
          </Flex>
        </Flex>
      </Media>

      <Media lessThan="lg" style={{ width: "100%" }}>
        <Box mt={4} mb={3}>
          {title && (
            <Box fontSize={24} fontWeight={600}>
              {title}
            </Box>
          )}
          <Flex className="gap-2" flexDirection="column" my={2}>
            <Flex className="gap-2">
              {statusFilter && (
                <Filter
                  menuPosition="bottom-right"
                  fullHeight={true}
                  button={
                    <FilterButton
                      style={borderStyle}
                      rightIcon={<SettingSvg />}
                      text={currentStatus?.text}
                    />
                  }
                  items={StatusFilterOptions}
                  isSelectedFn={isSelectStatusFn}
                  multiple={false}
                  onSelect={onChangeStatus}
                  viewMode="radio"
                />
              )}
              {scopeFilter && (
                <Filter
                  className="flex-1!"
                  menuPosition="bottom-right"
                  fullHeight={true}
                  button={
                    <FilterButton
                      style={borderStyle}
                      rightIcon={<SettingSvg />}
                      text={currentScope?.text}
                    />
                  }
                  items={availableScopeFiltersOptions}
                  isSelectedFn={isSelectScopeFn}
                  multiple={false}
                  onSelect={onChangeScope}
                  viewMode="radio"
                />
              )}
              {allowAdd && (
                <Button
                  className="flex-1!"
                  intent="primary"
                  icon="plus"
                  style={{ fontSize: 15 }}
                  onClick={() => {
                    navigate(buildCreateScenarioUrl());
                  }}
                >
                  Create Scenario
                </Button>
              )}
            </Flex>
            {onSearch && (
              <SearchBox
                style={{
                  borderRadius: 6,
                  border: "0.5px solid #E2E2E2",
                  overflow: "hidden",
                  padding: "22px 0px 22px 36px",
                  fontSize: 16,
                }}
                className="icon-center"
                disabled={loading}
                refine={onSearch}
                throttledDuration={400}
                searchPlaceholder="Search scenarios"
              />
            )}
          </Flex>
        </Box>
      </Media>
    </>
  );
}
